import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound, useParams } from "@tanstack/react-router"
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { PencilIcon, Plus, Trash2Icon } from "lucide-react"
import { useMemo } from "react"

import type { GetUnitMemberResponse } from "@repo/supabase/database-types"
import { ActionsDropdownMenu } from "@repo/ui/components/ui/actions-dropdown-menu"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { DataTable } from "@repo/ui/components/ui/data-table"
import {
  getClientMembersQueryOptions,
  getExtendedClientQueryOptions,
} from "~/modules/clients/clients.queries"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/clients/$clientSlug/members",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(
        getExtendedClientQueryOptions({ clientSlug: params.clientSlug }),
      ),
      context.queryClient.ensureQueryData(
        getClientMembersQueryOptions({ clientSlug: params.clientSlug }),
      ),
    ])
  },
})

const columnHelper = createColumnHelper<GetUnitMemberResponse>()

function RouteComponent() {
  const params = useParams({ from: Route.id })
  const { isClientMembershipOn } = useFeatureConfigs()

  if (!isClientMembershipOn) {
    throw notFound()
  }

  const { data: client } = useSuspenseQuery(
    getExtendedClientQueryOptions({ clientSlug: params.clientSlug }),
  )
  const { data: members = [] } = useSuspenseQuery(
    getClientMembersQueryOptions({ clientSlug: params.clientSlug }),
  )

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("name", {
          header: "Name",
          cell: ({ getValue }) => (
            <div className="font-medium text-white">{getValue()}</div>
          ),
        }),
        columnHelper.accessor("email", {
          header: "Email",
          cell: ({ getValue }) => (
            <div className="text-white/70">{getValue()}</div>
          ),
        }),
        columnHelper.accessor("role_name", {
          header: "Role",
          cell: ({ getValue }) => {
            const role = getValue()
            return (
              <Badge
                variant={
                  role.toLocaleLowerCase().includes("admin")
                    ? "default"
                    : "outline"
                }
              >
                {role}
              </Badge>
            )
          },
          meta: {
            filterVariant: "select",
            filterSelectOptions: [
              { label: "Admin", value: "Admin" },
              { label: "Viewer", value: "Viewer" },
            ],
          },
        }),
        columnHelper.display({
          id: "actions",
          header: () => <div className="grow text-right">Actions</div>,
          cell: ({ row }) => (
            <div className="flex justify-end">
              <ActionsDropdownMenu
                groups={[
                  {
                    actions: [
                      {
                        label: "Edit Member",
                        icon: PencilIcon,
                        onClick: () =>
                          alert(
                            `Not implemented yet.\n\nEdit member "${row.original.user_id}"`,
                          ),
                      },
                      {
                        label: "Remove Member",
                        icon: Trash2Icon,
                        onClick: () =>
                          alert(
                            `Not implemented yet.\n\nRemove member "${row.original.user_id}"`,
                          ),
                        variant: "destructive",
                      },
                    ],
                  },
                ]}
              />
            </div>
          ),
        }),
      ] as ColumnDef<GetUnitMemberResponse>[],
    [],
  )

  if (!client) {
    return <div>Client not found</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-semibold text-lg text-white">Members</h1>
        <Button
          size="sm"
          variant="glass-darker"
          onClick={() =>
            alert(
              `Not implemented yet.\n\nAdd member for client "${params.clientSlug}".`,
            )
          }
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Member
        </Button>
      </div>

      <DataTable
        data={members}
        columns={columns}
        searchPlaceholder="Search members..."
        noResultsMessage="No members found"
        manualPagination={false}
        manualSorting={false}
        manualFiltering={false}
      />
    </div>
  )
}
